import { inject, Injectable } from '@angular/core';

import { AccessKey, DataRecord, WorkflowState } from './workflow-state.service';
import { CookieService } from './cookie.service';
import { JoseService } from './jose.service';

const ENCRYPTED_PROVIDERS = new Set(['dummy202', 'dummy200', 'volvo', 'qlocx']);

@Injectable({ providedIn: 'root' })
export class AccessKeyProcessor {
  private readonly cookies = inject(CookieService);
  private readonly jose = inject(JoseService);
  private readonly workflow = inject(WorkflowState);

  async process(
    response: DataRecord,
    itemMap: ReadonlyMap<string, string> = new Map(),
    keyUserCache: Readonly<Record<string, string>> = {},
  ): Promise<DataRecord> {
    const deliveryItems = Array.isArray(response['deliveryItems']) ? response['deliveryItems'] : [];
    const processedItems: DataRecord[] = [];
    const flattened = new Map<string, AccessKey>();

    for (const rawDeliveryItem of deliveryItems) {
      if (!isRecord(rawDeliveryItem)) {
        continue;
      }

      const rawKeys = Array.isArray(rawDeliveryItem['accessKeys'])
        ? rawDeliveryItem['accessKeys']
        : [];
      const accessKeys: AccessKey[] = [];

      for (const rawKey of rawKeys) {
        if (!isRecord(rawKey)) {
          continue;
        }

        const itemIds = Array.isArray(rawKey['itemIds']) ? rawKey['itemIds'] : [];
        const itemIdValue = itemIds.at(-1);
        const itemId = typeof itemIdValue === 'string' ? itemIdValue : '';
        const mapId = itemMap.get(itemId) ?? this.cookies.read(itemId) ?? '';
        const metadata = this.workflow.archivedLockedSpaces().get(mapId);
        const provider = String(rawKey['systemProvider'] ?? metadata?.['systemProvider'] ?? '');
        const accessKey: AccessKey = {
          ...rawKey,
          displayName: metadata?.['displayName'],
          systemProvider: provider,
          itemId,
          keyUserId: metadata?.['keyUserId'] ?? keyUserCache[itemId],
          lockedSpaceId:
            typeof rawKey['lockedSpaceId'] === 'string' ? rawKey['lockedSpaceId'] : mapId,
        };

        if (
          !accessKey['reason'] &&
          ENCRYPTED_PROVIDERS.has(provider.toLowerCase()) &&
          typeof accessKey['accessKey'] === 'string'
        ) {
          accessKey['encryptedKey'] = accessKey['accessKey'];
          accessKey['accessKey'] = await this.jose.decrypt(accessKey['accessKey']);
        }

        const accessId = accessKey['accessId'];
        if (!accessKey['reason'] && typeof accessId === 'string') {
          flattened.set(accessId, accessKey);
        }
        accessKeys.push(accessKey);
      }

      processedItems.push({ ...rawDeliveryItem, accessKeys });
    }

    this.workflow.replaceAccessKeys(flattened);
    return { ...response, deliveryItems: processedItems };
  }
}

function isRecord(value: unknown): value is DataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}